import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Users, Globe2, Trophy, Youtube } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { EmailAuthModal } from "../components/EmailAuthModal";

const FEATURES = [
  { img: "landingpage_screenshot01.webp", icon: Users, color: "#FF007F", title: "Create Your Next Champion",
    text: "Train your wrestlers, level up, gear up, and turn your roster into championship material." },
  { img: "landingpage_screenshot02.webp", icon: Globe2, color: "#00F0FF", title: "Take Your Wrestlers On Tour",
    text: "Send your wrestlers on the road, perform in iconic arenas, attract new fans, and grow your reach." },
  { img: "landingpage_screenshot03.webp", icon: Trophy, color: "#FFD700", title: "Step Into The Ring Against Other Players",
    text: "Put your roster to the test against other players, climb the rankings, and crush the competition." },
];

// Deterministic sparkle field (left%, top%, size px, delay s, duration s, color)
const SPARKLES = Array.from({ length: 42 }, (_, i) => {
  const r = (n) => ((Math.sin(i * 12.9898 + n * 78.233) * 43758.5453) % 1 + 1) % 1;
  return [r(1) * 100, r(2) * 100, 2 + r(3) * 3, r(4) * 6, 3 + r(5) * 4, ["#FF007F", "#00F0FF", "#FFFFFF", "#B44DFF"][i % 4]];
});

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const GoogleG = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

function AliveLayer() {
  return (
    <div className="lp-fade-in pointer-events-none fixed inset-0 z-[1] overflow-hidden" style={{ animationDelay: "0.3s" }} aria-hidden="true">
      <div className="lp-beam lp-beam-l" />
      <div className="lp-beam lp-beam-r" />
      <div className="lp-beam lp-beam-c" />
      {SPARKLES.map(([l, t, s, d, dur, c], i) => (
        <span key={i} className="lp-spark absolute rounded-full"
              style={{ left: `${l}%`, top: `${t}%`, width: s, height: s, background: c, boxShadow: `0 0 ${s * 3}px ${c}`, animationDelay: `${d}s`, animationDuration: `${dur}s` }} />
      ))}
    </div>
  );
}

const ORIGINS = ["bottom left", "bottom center", "bottom right"];

function FeatureCard({ f, i }) {
  const Icon = f.icon;
  const imgRef = useRef(null);
  // Hover = grow to the image's natural size (capped to the viewport).
  const zoom = (on) => {
    const el = imgRef.current;
    if (!el || !window.matchMedia("(hover: hover) and (min-width: 768px)").matches) return;
    const k = Math.min(el.naturalWidth / el.clientWidth, (window.innerHeight * 0.92) / el.clientHeight, (window.innerWidth * 0.62) / el.clientWidth);
    el.style.transform = on ? `scale(${Math.max(1, k)})` : "";
    el.style.zIndex = on ? 50 : "";
  };
  return (
    <div className="lp-rise relative z-0 hover:z-50 flex flex-col items-center text-center" style={{ animationDelay: `${0.75 + i * 0.12}s` }} data-testid={`landing-feature-${i + 1}`}>
      <img ref={imgRef} src={`/landingpage/${f.img}`} alt={f.title} draggable={false} data-testid={`landing-shot-${i + 1}`}
           onMouseEnter={() => zoom(true)} onMouseLeave={() => zoom(false)}
           className="lp-shot relative h-auto cursor-zoom-in transition-transform duration-300 ease-out"
           style={{ width: "min(100%, 52vh)", transformOrigin: ORIGINS[i] }} />
      <div className="mt-1 flex items-center justify-center gap-2">
        <Icon size={26} strokeWidth={2.4} className="shrink-0" style={{ color: f.color, filter: `drop-shadow(0 0 8px ${f.color})` }} />
        <h3 className="font-graffiti uppercase text-base md:text-[clamp(14px,1.15vw,22px)] md:whitespace-nowrap leading-tight" style={{ color: f.color, textShadow: `0 0 14px ${f.color}99, 2px 2px 0 #000` }}>
          {f.title}
        </h3>
      </div>
      <p className="mt-0.5 max-w-[400px] font-mono text-xs text-[#D9CFFF] leading-snug" style={{ textShadow: "0 1px 4px #000" }}>{f.text}</p>
    </div>
  );
}

export default function LoginPage() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [emailOpen, setEmailOpen] = useState(false);
  const [guestBusy, setGuestBusy] = useState(false);
  const playGuest = async () => {
    setGuestBusy(true);
    try {
      const r = await api.post("/auth/guest");
      await onEmailDone(r.data);
    } catch { setGuestBusy(false); }
  };
  const onEmailDone = async (data) => {
    try {
      const ref = localStorage.getItem("pending_referral_code");
      if (ref) {
        await api.post("/referral/attach", { code: ref }).catch(() => {});
        localStorage.removeItem("pending_referral_code");
      }
    } catch {}
    await refresh();
    nav(data.needs_name ? "/setup-name" : "/roster", { replace: true });
  };

  // Capture ?ref=CODE from the landing URL and stash it for post-signup attach.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const ref = q.get("ref");
    if (ref && /^[A-Z0-9]{4,16}$/i.test(ref)) {
      try { localStorage.setItem("pending_referral_code", ref.toUpperCase()); } catch {}
    }
  }, [location.search]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/roster";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0A051A]" data-testid="landing-page">
      <div className="lp-bg-in fixed inset-0 z-0 bg-cover bg-top" style={{ backgroundImage: "url('/landingpage/landingpage_background.webp')" }} aria-hidden="true" />
      <div className="lp-fade-in fixed inset-0 z-0" style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(10,5,26,0.15) 0%, rgba(10,5,26,0.55) 70%, rgba(10,5,26,0.85) 100%)" }} aria-hidden="true" />
      <AliveLayer />

      <main className="relative z-10 flex flex-col items-center px-5 pt-6 pb-4 min-h-screen md:h-[100dvh] md:min-h-0 md:pt-[2.5vh] md:pb-[1.5vh]">
        <div className="lp-presents font-mono text-[10px] sm:text-xs tracking-[0.45em] text-[#00F0FF] text-center" style={{ textShadow: "0 0 8px #00F0FF" }} data-testid="landing-presents">
          WRESTLING GAME NETWORK PRESENTS
        </div>
        <img src="/landingpage/landingpage_ringkings_logo.webp" alt="Wrestling Ring Kings" draggable={false} data-testid="login-logo"
             className="lp-logo mt-2 mb-[1.8vh] w-auto max-w-[82vw] h-[clamp(120px,25vh,300px)]" />

        <div className="lp-rise w-full max-w-[360px] flex flex-col items-center mt-1" style={{ animationDelay: "0.5s" }}>
          <button data-testid="google-login-btn" onClick={handleLogin}
                  className="lp-google relative overflow-hidden w-full h-[54px] rounded-xl font-heading font-black uppercase tracking-[0.18em] text-sm text-white flex items-center justify-center gap-3">
            <span className="lp-google-sweep absolute inset-y-0 -left-1/3 w-1/3" aria-hidden="true" />
            <GoogleG />
            <span className="relative">Sign in with Google</span>
          </button>
          <div className="mt-3 font-mono text-[10px] sm:text-[11px] tracking-[0.14em] whitespace-nowrap text-[#C9BDF0] text-center" style={{ textShadow: "0 1px 4px #000" }} data-testid="landing-tagline">
            NO PASSWORD · ONE CLICK · ENTER THE RING
          </div>
          <div className="w-full flex items-center gap-3 my-[1.4vh]" data-testid="landing-or">
            <div className="h-px flex-1" style={{ background: "linear-gradient(90deg, transparent, #A89BCC)" }} />
            <span className="font-mono text-[11px] tracking-[0.3em] text-[#A89BCC]">OR</span>
            <div className="h-px flex-1" style={{ background: "linear-gradient(90deg, #A89BCC, transparent)" }} />
          </div>
          <button data-testid="create-account-btn" onClick={() => setEmailOpen(true)}
                  className="w-[86%] h-[46px] rounded-xl border-2 border-[#00F0FF] bg-[#0A051A]/70 backdrop-blur font-heading font-bold uppercase tracking-[0.18em] text-xs text-[#00F0FF] flex items-center justify-center gap-2 transition-[background-color,box-shadow,transform] hover:bg-[#00F0FF]/15 hover:shadow-[0_0_20px_#00F0FF88] active:scale-95"
                  style={{ boxShadow: "0 0 12px #00F0FF55, inset 0 0 10px #00F0FF22" }}>
            <Mail size={16} strokeWidth={2.6} /> Create Account / Log In
          </button>
          <button data-testid="play-guest-btn" onClick={playGuest} disabled={guestBusy}
            className="mt-3 h-[38px] px-6 rounded-lg border border-[#A78BFA]/70 bg-[#21143D]/80 backdrop-blur font-heading font-bold uppercase tracking-[0.16em] text-[11px] text-[#E0D5FF] inline-flex items-center justify-center gap-2 transition-[background-color,box-shadow,transform] hover:bg-[#A78BFA]/20 hover:border-[#C4B5FD] hover:shadow-[0_0_14px_#A78BFA55] active:scale-95 disabled:opacity-50"
            style={{ boxShadow: "0 0 8px #A78BFA22" }}>
            {guestBusy ? "Starting…" : "Play as guest"}
          </button>
        </div>
        {emailOpen && <EmailAuthModal onClose={() => setEmailOpen(false)} onDone={onEmailDone} />}

        <section className="mt-10 md:mt-auto w-full max-w-[1600px] grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6" data-testid="landing-features">
          {FEATURES.map((f, i) => <FeatureCard key={f.img} f={f} i={i} />)}
        </section>

        <footer className="lp-rise mt-8 md:mt-[1.6vh] flex flex-col items-center gap-1.5" style={{ animationDelay: "1.1s" }}>
          <div className="flex items-center gap-3" data-testid="landing-socials">
            <a href="https://x.com/WrestlingGameN" target="_blank" rel="noopener noreferrer" data-testid="social-x" aria-label="Wrestling Game Network on X"
               className="lp-social inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FFFFFF55] bg-[#0A051A]/70 text-white font-mono text-[11px] tracking-wider">
              <XIcon width={14} height={14} /> @WrestlingGameN
            </a>
            <a href="https://www.youtube.com/@WrestlingGameNetwork" target="_blank" rel="noopener noreferrer" data-testid="social-youtube" aria-label="Wrestling Game Network on YouTube"
               className="lp-social inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF003355] bg-[#0A051A]/70 text-white font-mono text-[11px] tracking-wider">
              <Youtube size={16} className="text-[#FF0033]" /> YouTube
            </a>
          </div>
          <div className="flex justify-center gap-3 font-mono text-[10px] tracking-wider text-[#8C7FB5]" data-testid="login-legal-links">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="link-privacy">Privacy Policy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="link-terms">Terms of Service</Link>
            <span>·</span>
            <Link to="/legal" className="hover:text-white transition-colors" data-testid="link-legal">Legal Notice</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
